/**
 * The access app's flows (`release-access`): forgot password, the
 * sign-in second factor, and the "This wasn't me" freeze. Each is a state box
 * the Angular pages mirror into signals; every call, rule and message lives
 * here or in `@dealr/core-account`. Platform pieces (WebAuthn, the clock, the
 * device fingerprint) arrive as ports.
 */

import { accountErrorCode, accountErrorMessage, toAccountApiError, type LoginApi, type LoginMfaApi, type ResetLinkApi } from '@dealr/api-account';
import type { LoginMfaMethod, LoginSuccessBody, ResetVerificationResponse } from '@dealr/core-account/domain';
import {
  isPasswordResetRequired,
  FREEZE_STEPS,
  SIM_SWAP_WARNING,
  accessErrorView,
  activationSteps,
  isActivationLink,
  forgotPasswordSteps,
  forgotUsernameRequest,
  forgotUsernameSentMessage,
  groupMethodsByStrength,
  identityProgress,
  isPasswordStrong,
  isValidMethodCode,
  loginDestination,
  loginEnrollmentRequired,
  loginMfaPending,
  methodDisplayName,
  methodSecondary,
  methodTypeLabel,
  normalizeAccessToken,
  passwordStrengthMessages,
  RECOVERY_REQUEST_STARTED,
  SESSION_APPROVAL_DENIED,
  SESSION_APPROVAL_METHOD_UUID,
  matchNumberPrompt,
  backupCodeMethod,
  lostDeviceCaptureToken,
  recoveryOptions,
  recoveryRequestStarted,
  resetRequirement,
  signInSteps,
  stepUpPrompt,
  stepperView,
  type AccessErrorView,
  type IdentityProgress,
  type LoginMfaPending,
  type RecoveryContext,
  type RecoveryOptionType,
  type RecoveryOptionView,
  type ResetRequirement,
  type StepperView,
} from '@dealr/core-account';
import type { WebAuthnPort } from './ports';
import { createLoginMfaSession, type LoginMfaSession } from './loginMfa';
import { SecondFactorError } from './secondFactor';
import { createStateBox, type Observable } from './store';

/** The host's timer, so polling stays on its scheduler and tests run instantly. */
export interface AccessClock {
  delay(ms: number): PromiseLike<unknown>;
  now(): number;
}

/** A failed call as the page reads it. */
export function accessError(error: unknown, context: 'link' | 'form', fallback = ''): AccessErrorView {
  if (error instanceof SecondFactorError) {
    return { kind: 'invalid', title: error.cancelled ? 'Cancelled' : 'That didn’t work', message: error.message, actions: ['retry'] };
  }
  const apiError = toAccountApiError(error);
  return accessErrorView(apiError.status, accountErrorMessage(error, fallback), context);
}

// ── Second-factor picker (shared by reset and sign-in) ─────────────────────

export interface MfaPickerState {
  /** Strongest first. */
  strong: LoginMfaMethod[];
  /** Filed under "Less secure options", with the SIM-swap warning. */
  weak: LoginMfaMethod[];
  weakWarning: string;
  backup: LoginMfaMethod | null;
  sessionApproval: LoginMfaMethod | null;
  method: LoginMfaMethod | null;
  needsCode: boolean;
  sent: boolean;
  code: string;
  codeLooksValid: boolean;
  prompt: string;
  /** Waiting for another signed-in device to approve. */
  waiting: boolean;
  /** While waiting: the number the other device must pick, shown large. Null until the prompt exists. */
  matchNumber: number | null;
  /** "Lost your phone?": the server's recovery options for this prompt. */
  recovery: RecoveryOptionView[];
  /** A lost-device path in progress: a request that started, or an ID check to embed. */
  lost: LostDeviceView | null;
}

export interface LostDeviceView {
  /** `request-started`: done here, wait for email. `identity`: embed the check. */
  status: 'request-started' | 'identity';
  captureToken: string | null;
  message: string;
}

export function methodName(method: LoginMfaMethod): string {
  return methodDisplayName(method);
}

export function methodKind(method: LoginMfaMethod): string {
  return methodTypeLabel(method.type);
}

/**
 * How a sign-in method reads as a choice card: an icon key (`@dealr/ng-ui`
 * `DealrIconName`), the method TYPE as the title, one generic line, and the
 * secondary line (masked phone, the user's nickname as "Named: …"). Never a
 * device, OS or vendor name.
 */
export interface MethodChoice {
  icon: 'fingerprint' | 'phone-app' | 'message' | 'phone' | 'ticket' | 'devices' | 'key';
  title: string;
  detail: string;
  secondary: string;
}

const METHOD_COPY: Record<string, { icon: MethodChoice['icon']; detail: string }> = {
  webauthn: { icon: 'fingerprint', detail: 'Use your fingerprint, face, screen lock or a security key.' },
  totp: { icon: 'phone-app', detail: 'Enter the 6-digit code from your authenticator app.' },
  sms: { icon: 'message', detail: 'We text you a 6-digit code.' },
  voice: { icon: 'phone', detail: 'We call you and read out a code.' },
  backup_code: { icon: 'ticket', detail: 'One of the codes you saved when you set up sign-in.' },
  session_approval: { icon: 'devices', detail: 'Somewhere you’re already signed in. Tap Approve there.' },
};

export function methodChoice(method: LoginMfaMethod): MethodChoice {
  const copy = METHOD_COPY[method.type] ?? { icon: 'key' as const, detail: methodTypeLabel(method.type) };
  return { icon: copy.icon, title: methodTypeLabel(method.type), detail: copy.detail, secondary: method.type === 'session_approval' ? '' : methodSecondary(method) };
}

/** The fastest strong method gets the "Fastest" chip: a passkey when there is one, else the first strong method. */
export function fastestMethod(strong: readonly LoginMfaMethod[]): LoginMfaMethod | null {
  return strong.find((method) => method.type === 'webauthn') ?? strong[0] ?? null;
}

export const RECOVERY_ICONS: Record<RecoveryOptionType, 'ticket' | 'devices' | 'id-card' | 'person'> = {
  backup_code: 'ticket',
  session_approval: 'devices',
  identity_check: 'id-card',
  recovery_request: 'person',
};

function pickerState(session: LoginMfaSession, pending: LoginMfaPending, context: RecoveryContext): MfaPickerState {
  const { strong, weak } = groupMethodsByStrength(session.methods, pending.methodStrength);
  return {
    strong,
    weak,
    weakWarning: SIM_SWAP_WARNING,
    backup: session.backupMethod,
    sessionApproval: session.sessionApprovalMethod,
    method: null,
    needsCode: false,
    sent: false,
    code: '',
    codeLooksValid: false,
    prompt: '',
    waiting: false,
    matchNumber: null,
    recovery: recoveryOptions(pending.recoveryOptions, context),
    lost: null,
  };
}

function withMethod(state: MfaPickerState, patch: Partial<MfaPickerState>): MfaPickerState {
  const next = { ...state, ...patch };
  const m = next.method;
  return {
    ...next,
    codeLooksValid: !!m && isValidMethodCode(m.type, next.code),
    prompt: !m ? '' : next.waiting ? waitingPrompt(next.matchNumber) : stepUpPrompt(m),
  };
}

/** "Pick 47 on your other device." while a number is known, else the plain ask. */
function waitingPrompt(matchNumber: number | null): string {
  return matchNumber !== null ? `${matchNumberPrompt(matchNumber)}.` : 'Approve this sign-in on your other device. We’ll continue as soon as you do.';
}

/** The other device said no (or picked the wrong number): say so plainly. */
function approvalError(error: unknown): AccessErrorView | null {
  return accountErrorCode(error) === 'approval_denied'
    ? { kind: 'invalid', title: 'Sign-in denied', message: SESSION_APPROVAL_DENIED, actions: ['retry'] }
    : null;
}

interface PickerHost {
  state(): { mfa: MfaPickerState | null; busy: boolean };
  set(patch: { mfa?: MfaPickerState | null; busy?: boolean; error?: AccessErrorView | null; announcement?: string }): void;
  passed(body: LoginSuccessBody | null): void;
  /**
   * After the embedded lost-device ID check ends: `passed` moves on (the
   * host's own way), `pending` keeps waiting, a rejection means it failed.
   */
  identityDone(): Promise<'passed' | 'pending'>;
}

/** The picker's commands over a `LoginMfaSession`; the flows expose them. */
function pickerCommands(host: PickerHost, session: () => LoginMfaSession | null, clock: AccessClock) {
  let cancelled = false;
  const mfa = () => host.state().mfa;
  const patchMfa = (patch: Partial<MfaPickerState>) => {
    const current = mfa();
    if (current) host.set({ mfa: withMethod(current, patch) });
  };
  return {
    async choose(method: LoginMfaMethod): Promise<void> {
      const s = session();
      if (!s || host.state().busy) return;
      cancelled = false;
      host.set({ busy: true, error: null });
      patchMfa({ method, needsCode: false, sent: false, code: '', waiting: method === s.sessionApprovalMethod, matchNumber: null });
      try {
        if (method === s.sessionApprovalMethod) {
          const body = await s.waitForSessionApproval({
            delay: (ms) => clock.delay(ms),
            now: () => clock.now(),
            cancelled: () => cancelled,
            matchNumber: (matchNumber) => {
              patchMfa({ matchNumber });
              if (matchNumber !== null) host.set({ announcement: `${matchNumberPrompt(matchNumber)}.` });
            },
          });
          host.set({ busy: false });
          if (body) host.passed(body);
          else patchMfa({ method: null, waiting: false, matchNumber: null });
          return;
        }
        const result = await s.choose(method);
        host.set({ busy: false });
        if (result.done) {
          host.passed(result.done);
          return;
        }
        const sent = result.step.needsCode && result.step.sent;
        patchMfa({ needsCode: true, sent });
        if (sent) host.set({ announcement: 'We sent you a code.' });
      } catch (error) {
        patchMfa({ waiting: false, matchNumber: null });
        host.set({ busy: false, error: approvalError(error) ?? accessError(error, 'form', 'We could not start that method. Try another one.') });
      }
    },
    setCode(code: string): void {
      patchMfa({ code });
    },
    async submitCode(): Promise<void> {
      const s = session();
      const m = mfa();
      if (!s || !m || !m.method || host.state().busy) return;
      if (!m.codeLooksValid) {
        host.set({ error: { kind: 'invalid', title: 'Check the code', message: 'Enter the code exactly as shown.', actions: ['retry'] } });
        return;
      }
      host.set({ busy: true, error: null });
      try {
        const body = await s.submitCode(m.method, m.code);
        host.set({ busy: false });
        host.passed(body);
      } catch (error) {
        patchMfa({ code: '' });
        host.set({ busy: false, error: accessError(error, 'form', 'That code did not work. Try again.') });
      }
    },
    /** Back to the method list; also stops a session-approval wait. */
    another(): void {
      cancelled = true;
      patchMfa({ method: null, needsCode: false, code: '', waiting: false, matchNumber: null });
      host.set({ error: null });
    },
    cancelWait(): void {
      cancelled = true;
    },
    /** "Lost your phone?": run one of the server's recovery options. */
    async recover(type: RecoveryOptionType): Promise<void> {
      const s = session();
      if (!s || host.state().busy) return;
      if (type === 'backup_code') return this.choose(s.backupMethod ?? (backupCodeMethod() as unknown as LoginMfaMethod));
      if (type === 'session_approval') {
        return this.choose(s.sessionApprovalMethod ?? ({ uuid: SESSION_APPROVAL_METHOD_UUID, type: 'session_approval' } as LoginMfaMethod));
      }
      host.set({ busy: true, error: null });
      try {
        const reply = await s.recover(type);
        host.set({ busy: false });
        const captureToken = lostDeviceCaptureToken(reply);
        if (captureToken) {
          patchMfa({ lost: { status: 'identity', captureToken, message: LOST_DEVICE_IDENTITY_MESSAGE } });
          host.set({ announcement: 'Confirm it’s you with your ID below.' });
        } else if (recoveryRequestStarted(reply)) {
          patchMfa({ lost: { status: 'request-started', captureToken: null, message: RECOVERY_REQUEST_STARTED } });
          host.set({ announcement: RECOVERY_REQUEST_STARTED });
        } else {
          host.set({ error: accessErrorView(500, 'That option didn’t start. Try another one.', 'form') });
        }
      } catch (error) {
        host.set({ busy: false, error: accessError(error, 'form', 'That option didn’t start. Try another one.') });
      }
    },
    /** The embedded ID check ended (or "I’ve finished"): ask the server how it went. */
    async identityFinished(): Promise<void> {
      const m = mfa();
      if (!m || !m.lost || m.lost.status !== 'identity' || host.state().busy) return;
      host.set({ busy: true, error: null });
      try {
        const result = await host.identityDone();
        host.set({ busy: false });
        if (result === 'pending') {
          patchMfa({ lost: { ...m.lost, message: 'We’re still checking your ID. This usually takes a few seconds; check again in a moment.' } });
          host.set({ announcement: 'Still checking.' });
        }
      } catch (error) {
        patchMfa({ lost: null });
        host.set({
          busy: false,
          error: { kind: 'invalid', title: 'The ID check didn’t pass', message: 'You can try the check again or choose another option below.', actions: [] },
        });
      }
    },
    /** Back to the method list from a lost-device path. */
    leaveLost(): void {
      patchMfa({ lost: null });
      host.set({ error: null });
    },
  };
}

export const ADMIN_REQUESTED_MESSAGE =
  'We asked your dealership admin to approve resetting your sign-in. Once they approve it, request a new reset link: it will let you choose a password without an ID check.';

const LOST_DEVICE_IDENTITY_MESSAGE = 'Confirm it’s you with a photo of your ID and a quick face check. When it’s done, this page moves on.';

type PickerCommands = ReturnType<typeof pickerCommands>;

// ── Forgot password ─────────────────────────────────────────────────────────

/** `welcome` and `enroll` belong to account activation (invite links). */
export type ForgotStep = 'request' | 'link' | 'welcome' | 'verify' | 'password' | 'enroll' | 'done';

export interface ForgotPasswordState {
  step: ForgotStep;
  stepper: StepperView;
  busy: boolean;
  /** Loading the link's requirement. */
  loading: boolean;
  error: AccessErrorView | null;
  /** For a polite live region. */
  announcement: string;
  /** The server's confirmation after a link was requested. */
  sentMessage: string;
  /** When another reset email may be requested (clock ms); null when it may now. */
  resendAt: number | null;
  /** The username the last link went to, so the link step can resend without the form. */
  canResend: boolean;
  token: string | null;
  requirement: ResetRequirement | null;
  mfa: MfaPickerState | null;
  identity: IdentityProgress | null;
  /** Identity platform: a capture token the page embeds inline. */
  captureToken: string | null;
  /** The non-biometric route: the user asked their dealership admin to approve a recovery. */
  adminRequested: boolean;
  passwordProblems: string[];
  /** Where the signed-in tab goes after "Continue". */
  destination: string | null;
  /** The signed-in body `/changePass` returned, for the adapter's session handoff. */
  session: LoginSuccessBody | null;
  /** An invite link: welcome, password, then (when the session says so) two-step enrollment. */
  activation: boolean;
  /** `/changePass` sent the "password changed" email (only with `account-security`). */
  confirmationEmailed: boolean;
  /** The verification response's `invite` block, for the welcome line. */
  invite: ResetVerificationResponse['invite'] | null;
}

export interface ForgotPasswordDeps {
  /** `activation` for `/access/activate`; a reset link the server marks `activation` switches too. */
  mode?: 'reset' | 'activation';
  api: ResetLinkApi;
  webauthn: WebAuthnPort | null;
  clock: AccessClock;
  fingerprint(): PromiseLike<string>;
  origin: string;
  redirUrl?: string | null;
  /** A link was sent: lets the page hand the username and cooldown to the "sent" route. */
  onSent?(sent: { username: string; resendAt: number }): void;
}

export interface ForgotPasswordFlow extends Observable<ForgotPasswordState>, Omit<PickerCommands, 'cancelWait'> {
  requestLink(username: string): Promise<void>;
  /** Sends another link to the last username, once the cooldown is over. */
  resend(): Promise<void>;
  /**
   * A bookmarked or reloaded "check your email" step: shows it without sending another link.
   * `sent` is the request this tab just made on the previous page, so it can still resend.
   */
  resumeLinkSent(sent?: { username: string; resendAt: number } | null): void;
  /** Opening the emailed link: reads what it requires. Null token means a broken link. */
  openLink(rawToken: unknown): Promise<void>;
  startIdentityCheck(): Promise<void>;
  refreshIdentityCheck(): Promise<void>;
  /**
   * Instead of the ID check (or when none is possible): asks the user's
   * dealership admin to approve a recovery. Once it is approved, a new reset
   * link needs no further proof.
   */
  askAdmin(): Promise<void>;
  setPassword(password: string, confirm: string): Promise<void>;
  /** Activation: leave the welcome step for the proof (if any) or the password. */
  begin(): void;
  passwordHints(password: string): string[];
  destroy(): void;
}

/** The reset link's MFA endpoints in the login session's shape, so one session drives both. */
export function resetLinkMfaApi(api: ResetLinkApi, token: string): LoginMfaApi {
  return {
    challenge: (body) => api.verificationMfaChallenge(token, body.method_uuid),
    verify: (body) => {
      const proof: Record<string, unknown> = { ...body };
      delete proof['mfa_token'];
      return api.verificationMfaVerify(token, proof as never) as never;
    },
    recovery: (body) => api.verificationLostDevice(token, body.option),
  };
}

/** Matches the server's per-username cooldown (ResetEmailRateLimit::COOLDOWN_SECONDS). */
const RESEND_COOLDOWN_MS = 60_000;

/** `retry_after` from the reset-email limit's 429, else null. */
function resetRetryAfterSeconds(error: unknown): number | null {
  const apiError = toAccountApiError(error);
  const data = apiError.data as Record<string, unknown> | null;
  const seconds = apiError.status === 429 && data && typeof data === 'object' ? data['retry_after'] : null;
  return typeof seconds === 'number' && seconds > 0 ? seconds : null;
}

export function createForgotPasswordFlow(deps: ForgotPasswordDeps): ForgotPasswordFlow {
  let enroll = false;
  const steps = (requirement: ResetRequirement | null, activation: boolean) =>
    activation
      ? activationSteps({ verify: !!requirement && requirement.kind !== 'none', enroll })
      : forgotPasswordSteps(requirement ? requirement.kind : null);
  const startActivation = deps.mode === 'activation';
  const box = createStateBox<ForgotPasswordState>({
    step: startActivation ? 'welcome' : 'request',
    stepper: stepperView(steps(null, startActivation), startActivation ? 'welcome' : 'request'),
    busy: false,
    loading: false,
    error: null,
    announcement: '',
    sentMessage: '',
    resendAt: null,
    canResend: false,
    token: null,
    requirement: null,
    mfa: null,
    identity: null,
    captureToken: null,
    adminRequested: false,
    passwordProblems: [],
    destination: null,
    session: null,
    activation: startActivation,
    confirmationEmailed: false,
    invite: null,
  });
  let session: LoginMfaSession | null = null;
  const go = (step: ForgotStep, patch: Partial<ForgotPasswordState> = {}) => {
    const requirement = patch.requirement !== undefined ? patch.requirement : box.state.requirement;
    const activation = patch.activation !== undefined ? patch.activation : box.state.activation;
    const key = step === 'done' ? (enroll ? 'enroll' : 'password') : step;
    box.set({ ...patch, step, stepper: stepperView(steps(requirement, activation), key) });
  };

  const picker = pickerCommands(
    {
      state: () => box.state,
      set: (patch) => box.set(patch),
      passed: () => go('password', { mfa: null, announcement: 'Verified. Choose a new password.' }),
      async identityDone() {
        const token = box.state.token;
        if (!token) return 'pending';
        const data = await deps.api.verification(token);
        const status = typeof data?.identity_check === 'string' ? data.identity_check : 'pending';
        if (status === 'passed') {
          go('password', { mfa: null, announcement: 'Identity confirmed. Choose a new password.' });
          return 'passed';
        }
        if (status === 'pending' || status === 'not_started' || status === 'required') return 'pending';
        throw new SecondFactorError('The ID check didn’t pass.');
      },
    },
    () => session,
    deps.clock,
  );
  let lastUsername = '';

  const flow: ForgotPasswordFlow = {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    ...picker,
    async requestLink(username) {
      const name = (username || '').trim();
      if (box.state.busy) return;
      if (!name) {
        box.set({ error: { kind: 'invalid', title: 'Enter your username', message: 'We need your username to find the account.', actions: ['retry'] } });
        return;
      }
      if (box.state.resendAt !== null && deps.clock.now() < box.state.resendAt) return;
      box.set({ busy: true, error: null });
      try {
        const reply = await deps.api.requestLink(name);
        const sentMessage = typeof reply === 'string' && reply.trim() ? reply.trim() : 'If that username has an email on file, a reset link is on its way.';
        lastUsername = name;
        box.set({ busy: false, resendAt: deps.clock.now() + RESEND_COOLDOWN_MS, canResend: true });
        deps.onSent?.({ username: name, resendAt: box.state.resendAt as number });
        go('link', { sentMessage, announcement: sentMessage });
      } catch (error) {
        const retryAfter = resetRetryAfterSeconds(error);
        box.set({
          busy: false,
          error: accessError(error, 'form', 'We could not send a reset link.'),
          ...(retryAfter !== null ? { resendAt: deps.clock.now() + retryAfter * 1000 } : {}),
        });
      }
    },
    async resend() {
      if (lastUsername) await flow.requestLink(lastUsername);
    },
    resumeLinkSent(sent) {
      if (sent && sent.username) {
        lastUsername = sent.username;
        box.set({ resendAt: sent.resendAt, canResend: true });
      }
      if (box.state.step === 'request') go('link', { sentMessage: 'If that username has an email on file, a reset link is on its way.' });
    },
    async openLink(rawToken) {
      const token = normalizeAccessToken(rawToken);
      if (!token) {
        go('link', { token: null, error: accessErrorView(404, '', 'link') });
        return;
      }
      const entry: ForgotStep = box.state.activation ? 'welcome' : 'verify';
      go(entry, { token, loading: true, error: null });
      let requirement: ResetRequirement;
      let activation = box.state.activation;
      try {
        const data = await deps.api.verification(token);
        activation = activation || isActivationLink(data);
        if (data && data.invite) box.set({ invite: data.invite });
        requirement = resetRequirement(200, data);
      } catch (error) {
        const status = toAccountApiError(error).status;
        if (status !== 404) {
          go(entry, { loading: false, error: accessError(error, 'link', 'This link is no longer valid.') });
          return;
        }
        requirement = resetRequirement(404, null);
      }
      if (requirement.kind === 'mfa') {
        const pending = { ...requirement.pending, mfaToken: token };
        session = createLoginMfaSession(resetLinkMfaApi(deps.api, token), pending, deps.webauthn);
      }
      if (activation && (requirement.kind === 'none' || requirement.kind === 'mfa')) {
        go('welcome', { loading: false, requirement, activation, ...(session && requirement.kind === 'mfa' ? { mfa: pickerState(session, { ...requirement.pending, mfaToken: token }, 'reset') } : {}) });
        return;
      }
      if (requirement.kind === 'none') {
        go('password', { loading: false, requirement, activation });
        return;
      }
      if (requirement.kind === 'mfa' && session) {
        go('verify', { loading: false, requirement, activation, mfa: pickerState(session, { ...requirement.pending, mfaToken: token }, 'reset') });
        return;
      }
      if (requirement.kind === 'identity_check') {
        go('verify', { loading: false, requirement, activation, identity: identityProgress(requirement.status), captureToken: requirement.captureToken });
        return;
      }
      go('verify', { loading: false, requirement, activation });
    },
    async startIdentityCheck() {
      const token = box.state.token;
      if (!token || box.state.busy) return;
      box.set({ busy: true, error: null });
      try {
        const reply = await deps.api.identityCheck(token);
        const identity = identityProgress(reply.identity_check || 'pending');
        box.set({ busy: false, identity, announcement: identity.message });
        if (identity.passed) go('password', { announcement: 'Identity confirmed. Choose a new password.' });
      } catch (error) {
        box.set({ busy: false, error: accessError(error, 'link', 'The ID check could not be started.') });
      }
    },
    async refreshIdentityCheck() {
      return this.startIdentityCheck();
    },
    async askAdmin() {
      const token = box.state.token;
      if (!token || box.state.busy) return;
      box.set({ busy: true, error: null });
      try {
        await deps.api.verificationLostDevice(token, 'recovery_request');
        box.set({ busy: false, adminRequested: true, announcement: ADMIN_REQUESTED_MESSAGE });
      } catch (error) {
        box.set({ busy: false, error: accessError(error, 'link', 'Your request could not be sent.') });
      }
    },
    begin() {
      const st = box.state;
      if (st.step !== 'welcome' || !st.requirement) return;
      go(st.requirement.kind === 'mfa' ? 'verify' : 'password');
    },
    passwordHints: (password) => passwordStrengthMessages(password),
    async setPassword(password, confirm) {
      const token = box.state.token;
      if (!token || box.state.busy) return;
      const problems: string[] = [];
      if (!isPasswordStrong(password)) problems.push('The new password needs: ' + passwordStrengthMessages(password).join(', ').toLowerCase() + '.');
      if (password !== confirm) problems.push('The passwords do not match.');
      box.set({ passwordProblems: problems, error: null });
      if (problems.length) return;
      box.set({ busy: true });
      try {
        const fingerprint = await deps.fingerprint();
        const body = await deps.api.setPassword({ token, password, confirm_password: confirm, fingerprint });
        const destination = body && body.token ? loginDestination(body, { origin: deps.origin, redirUrl: deps.redirUrl }) : null;
        box.set({ busy: false });
        enroll = box.state.activation && loginEnrollmentRequired(body);
        const announcement = box.state.activation
          ? enroll ? 'Password saved. Next, set up two-step verification.' : 'Your account is active.'
          : 'Your password has been reset.';
        const confirmationEmailed = !!body && body.security_email_sent === true;
        go(enroll ? 'enroll' : 'done', { destination, session: body && body.token ? body : null, announcement, confirmationEmailed });
      } catch (error) {
        box.set({ busy: false, error: accessError(error, 'link', 'Your password could not be saved.') });
      }
    },
    destroy() {
      picker.cancelWait();
    },
  };
  return flow;
}

// ── Sign in ─────────────────────────────────────────────────────────────────

export type SignInStep = 'credentials' | 'device-code' | 'second-factor' | 'enroll' | 'done';

export interface SignInState {
  step: SignInStep;
  stepper: StepperView;
  busy: boolean;
  error: AccessErrorView | null;
  announcement: string;
  /** The 406 message: a code was emailed or texted to confirm this device. */
  deviceCodeMessage: string;
  /** Where the server sent the device code; it texts when the account has a phone. */
  deviceCodeChannel: 'email' | 'phone';
  mfa: MfaPickerState | null;
  /** The signed-in body, for the adapter's session handoff. */
  session: LoginSuccessBody | null;
  destination: string | null;
}

export interface SignInDeps {
  login: LoginApi;
  mfa: LoginMfaApi;
  webauthn: WebAuthnPort | null;
  clock: AccessClock;
  fingerprint(): PromiseLike<string>;
  origin: string;
  redirUrl?: string | null;
}

export interface SignInFlow extends Observable<SignInState>, Omit<PickerCommands, 'cancelWait'> {
  submitCredentials(username: string, password: string): Promise<void>;
  submitDeviceCode(code: string): Promise<void>;
  /** Back to the credentials form (also stops any wait). */
  restart(): void;
  destroy(): void;
}

function deviceCodeChannelOf(message: string): 'email' | 'phone' {
  return !/email/i.test(message) && /text|phone|sent to \(/i.test(message) ? 'phone' : 'email';
}

export function createSignInFlow(deps: SignInDeps): SignInFlow {
  let secondFactor = false;
  let enrollment = false;
  let session: LoginMfaSession | null = null;
  let credentials: { username: string; password: string } | null = null;
  const view = (step: SignInStep) => stepperView(signInSteps({ secondFactor, enrollment }), step === 'device-code' ? 'second-factor' : step === 'done' ? 'credentials' : step);
  const box = createStateBox<SignInState>({
    step: 'credentials',
    stepper: view('credentials'),
    busy: false,
    error: null,
    announcement: '',
    deviceCodeMessage: '',
    deviceCodeChannel: 'email',
    mfa: null,
    session: null,
    destination: null,
  });
  const go = (step: SignInStep, patch: Partial<SignInState> = {}) => box.set({ ...patch, step, stepper: view(step) });

  function signedIn(body: LoginSuccessBody | null): void {
    if (!body || !body.token) {
      box.set({ busy: false, error: accessErrorView(401, 'The entered information is incorrect.', 'form') });
      return;
    }
    credentials = null;
    const destination = loginDestination(body, { origin: deps.origin, redirUrl: deps.redirUrl });
    if (loginEnrollmentRequired(body) && body.type !== 'admin') {
      enrollment = true;
      go('enroll', { session: body, destination, mfa: null, announcement: 'Signed in. Set up two-step verification to continue.' });
      return;
    }
    go('done', { session: body, destination, mfa: null, announcement: 'Signed in.' });
  }

  const picker = pickerCommands(
    {
      state: () => box.state,
      set: (patch) => box.set(patch),
      passed: signedIn,
      async identityDone() {
        if (!session) return 'pending';
        const body = await session.identityCheckResult();
        if (!body) return 'pending';
        // A passed lost-device check is a one-time sign-in with every factor removed: enrollment comes next.
        signedIn(body);
        return 'passed';
      },
    },
    () => session,
    deps.clock,
  );

  async function attempt(extra: { identity_code?: string }): Promise<void> {
    if (!credentials) return;
    box.set({ busy: true, error: null });
    try {
      const fingerprint = await deps.fingerprint();
      const body = await deps.login.login({ ...credentials, fingerprint, ...extra });
      box.set({ busy: false });
      signedIn(body);
    } catch (error) {
      const apiError = toAccountApiError(error);
      box.set({ busy: false });
      const pending = loginMfaPending(apiError.status, apiError.data);
      if (pending) {
        secondFactor = true;
        session = createLoginMfaSession(deps.mfa, pending, deps.webauthn);
        go('second-factor', { mfa: pickerState(session, pending, 'login') });
        return;
      }
      if (isPasswordResetRequired(apiError.status, apiError.data)) {
        box.set({ error: accessErrorView(403, accountErrorMessage(error, ''), 'reset-required') });
        return;
      }
      if (apiError.status === 406) {
        secondFactor = true;
        const deviceCodeMessage = accountErrorMessage(error, 'Enter the code we emailed you to confirm this device.');
        go('device-code', { deviceCodeMessage, deviceCodeChannel: deviceCodeChannelOf(deviceCodeMessage) });
        return;
      }
      box.set({ error: accessError(error, 'form', 'The entered information is incorrect.') });
    }
  }

  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    ...picker,
    async submitCredentials(username, password) {
      if (box.state.busy) return;
      if (!username.trim() || !password) {
        box.set({ error: { kind: 'invalid', title: 'Enter your username and password', message: 'Both are required.', actions: ['retry'] } });
        return;
      }
      credentials = { username: username.trim(), password };
      await attempt({});
    },
    async submitDeviceCode(code) {
      if (box.state.busy) return;
      if (!code.trim()) {
        box.set({ error: { kind: 'invalid', title: 'Enter the code', message: box.state.deviceCodeChannel === 'phone' ? 'Check your phone for the texted code.' : 'Check your email for the code.', actions: ['retry'] } });
        return;
      }
      await attempt({ identity_code: code.trim() });
    },
    restart() {
      picker.cancelWait();
      credentials = null;
      session = null;
      secondFactor = false;
      enrollment = false;
      go('credentials', { mfa: null, error: null, busy: false });
    },
    destroy() {
      picker.cancelWait();
    },
  };
}

// ── Security freeze ─────────────────────────────────────────────────────────

export interface FreezeState {
  step: 'confirm' | 'result';
  stepper: StepperView;
  token: string | null;
  busy: boolean;
  frozen: boolean;
  error: AccessErrorView | null;
}

export interface FreezeFlow extends Observable<FreezeState> {
  confirm(): Promise<void>;
}

export function createSecurityFreezeFlow(api: Pick<ResetLinkApi, 'securityFreeze'>, rawToken: unknown): FreezeFlow {
  const token = normalizeAccessToken(rawToken);
  const box = createStateBox<FreezeState>({
    step: token ? 'confirm' : 'result',
    stepper: stepperView(FREEZE_STEPS, token ? 'confirm' : 'result'),
    token,
    busy: false,
    frozen: false,
    error: token ? null : accessErrorView(404, 'This freeze link has expired or was already used. If you still think someone else is in your account, reset your password now; that signs out every other device.', 'link'),
  });
  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    async confirm() {
      if (!token || box.state.busy || box.state.frozen) return;
      box.set({ busy: true, error: null });
      try {
        await api.securityFreeze(token);
        box.set({ busy: false, frozen: true, step: 'result', stepper: stepperView(FREEZE_STEPS, 'result') });
      } catch (error) {
        const view = accessError(error, 'link', 'The account could not be frozen.');
        const terminal = view.kind === 'expired-link';
        box.set({ busy: false, error: view, ...(terminal ? { step: 'result' as const, stepper: stepperView(FREEZE_STEPS, 'result') } : {}) });
      }
    },
  };
}

// ── Forgot username ────────────────────────────────────────────────────────

export interface ForgotUsernameState {
  step: 'request' | 'sent';
  busy: boolean;
  /** What to fix in the field, before anything is sent. */
  fieldError: string;
  error: AccessErrorView | null;
  channel: 'email' | 'phone' | null;
  sentMessage: string;
}

export interface ForgotUsernameFlow extends Observable<ForgotUsernameState> {
  request(value: string): Promise<void>;
  /** "Try a different email or phone": back to the form. */
  again(): void;
}

/** `product`: "Dealr" or "Dealer Title Solutions", for the sent message. */
export function createForgotUsernameFlow(api: Pick<ResetLinkApi, 'forgotUsername'>, product = 'Dealr'): ForgotUsernameFlow {
  const initial: ForgotUsernameState = { step: 'request', busy: false, fieldError: '', error: null, channel: null, sentMessage: '' };
  const box = createStateBox<ForgotUsernameState>(initial);
  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    async request(value) {
      if (box.state.busy) return;
      const parsed = forgotUsernameRequest(value);
      if ('error' in parsed) {
        box.set({ fieldError: parsed.error, error: null });
        return;
      }
      box.set({ busy: true, fieldError: '', error: null });
      try {
        await api.forgotUsername(parsed.body);
        box.set({ busy: false, step: 'sent', channel: parsed.channel, sentMessage: forgotUsernameSentMessage(product) });
      } catch (error) {
        box.set({ busy: false, error: accessError(error, 'form', 'We couldn’t send that right now.') });
      }
    },
    again() {
      box.set({ ...initial });
    },
  };
}
